/**
 * Section-properties drawer (Phase 3 of pb-section-properties overhaul).
 *
 * A side popover that slides in from the panel-left's right edge when an
 * inspector row is clicked. Hosts the actual edit UI (Background / Spacing /
 * Custom CSS / Decorative SVG / Form). Width defaults to the inspector size
 * and can be widened by richer design drawers; it is anchored to the
 * panel-left container, body-relative position so it floats over the canvas
 * without being clipped by the panel's overflow rules.
 *
 * Capabilities:
 *   - Single-active-drawer invariant
 *   - Slide-in animation (gated on prefers-reduced-motion)
 *   - Escape closes; click-outside closes (clicks back to the panel
 *     containing the trigger row are treated as "switch drawer")
 *   - Drawer-swap cross-fade — when a different row is opened while a drawer
 *     is already open, the body cross-fades and the active row marker moves;
 *     the drawer container itself persists
 *   - Focus trap; focus returns to the originating row when closed
 *   - Built on the component-kit drawer shell (`.pb-drawer-panel*`, Polish
 *     Phase 7): a section surface with its own visual identity (violet section
 *     edge), distinct from element-editing glass panels
 */

import { trapFocus } from '../focus-trap.js';
import { isOwnedPopoverTarget } from '../popover-menu.js';
import { createDrawerShell, attachSheetDrag, createSheetGrabber } from '../components.js';
import { computeSheetDetents, detentOffsets } from '../sheet.js';
import {
  isPhoneViewport,
  getViewportHeight,
  getKeyboardInset
} from '../../shared/viewport-service.js';

let _activeDrawer = null;
let _swapPending = false;

const DEFAULT_DRAWER_WIDTH = 280;
const MIN_DRAWER_WIDTH = 260;
const MAX_DRAWER_GUTTER = 16;
const ANCHOR_GAP = 12;
const ANIMATION_MS = 220;
const SWAP_FADE_MS = 120;
const SHEET_DETENT_NAMES = ['full', 'half', 'peek'];

function prefersReducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function normalizeDrawerWidth(width) {
  const parsed = Number.parseFloat(width);
  if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_DRAWER_WIDTH;
  return Math.max(MIN_DRAWER_WIDTH, Math.round(parsed));
}

function setDrawerWidth(drawer, width) {
  const drawerWidth = normalizeDrawerWidth(width);
  drawer.dataset.spiDrawerWidth = String(drawerWidth);
  drawer.style.width = `${drawerWidth}px`;
  drawer.style.maxWidth = `calc(100vw - ${MAX_DRAWER_GUTTER}px)`;
}

function getDrawerWidth(drawer) {
  const fromDataset = Number.parseFloat(drawer?.dataset?.spiDrawerWidth || '');
  const desired = Number.isFinite(fromDataset) ? fromDataset : DEFAULT_DRAWER_WIDTH;
  const viewportWidth = Number.isFinite(window.innerWidth)
    ? window.innerWidth
    : desired + MAX_DRAWER_GUTTER;
  return Math.min(desired, Math.max(MIN_DRAWER_WIDTH, viewportWidth - MAX_DRAWER_GUTTER));
}

function eventContainsNode(event, node) {
  if (!node) return false;
  const path = typeof event.composedPath === 'function' ? event.composedPath() : null;
  if (Array.isArray(path) && path.includes(node)) return true;
  return event.target instanceof Node ? node.contains(event.target) : false;
}

function isDrawerPointerInside(event, drawer, anchorEl) {
  if (eventContainsNode(event, drawer)) return true;
  if (isOwnedPopoverTarget(event.target, drawer)) return true;
  if (eventContainsNode(event, anchorEl)) return true;
  // Some native controls can emit events with non-Node targets. Treat those
  // as internal so color pickers/select menus do not collapse the sidebar.
  return !(event.target instanceof Node);
}

/** Usable height for the phone sheet: the visual viewport, less any keyboard. */
function usableSheetHeight() {
  const h = getViewportHeight() || window.innerHeight || 0;
  return Math.max(0, h - getKeyboardInset());
}

function applySheetDetent(drawer, name, heights = computeSheetDetents(usableSheetHeight())) {
  const detent = ['peek', 'half', 'full'].includes(name) ? name : 'half';
  const height = heights[detent] ?? heights.half;
  drawer.style.setProperty('--pb-sheet-h', `${Math.round(height)}px`);
  drawer.style.transform = '';
  drawer.dataset.pbDetent = detent;
}

function buildDrawerShell({ id, title, width }) {
  // Uses the shared component-kit drawer shell (Polish Phase 7): `.pb-drawer-panel*`
  // gives drawers a visual identity distinct from element-editing glass panels,
  // while keeping the `.pb-spi-drawer*` behavior hooks this module drives.
  const { drawer, titleId } = createDrawerShell({ id, title });

  // Mobile P13 — on a phone this drawer becomes a non-modal bottom sheet.
  //
  // ADDITIVE, not a rewrite: the same element, the same `.pb-spi-drawer*` hooks,
  // the same lifecycle below. What changes is that it stops being ANCHORED to a
  // row (there is no room beside one at 360px) and stops carrying an inline
  // width — which is what P8's four `!important` declarations existed to fight.
  // Those are deleted in this commit; the sheet CSS owns the geometry now.
  if (isPhoneViewport()) {
    drawer.classList.add('pb-sheet', 'pb-sheet--nonmodal');
    drawer.insertBefore(createSheetGrabber(), drawer.firstChild);
    drawer.querySelector('.pb-spi-drawer-header')?.classList.add('pb-sheet-header');
    drawer.querySelector('[data-spi-drawer-body]')?.classList.add('pb-sheet-body');
    applySheetDetent(drawer, 'half');
    return { drawer, titleId, isSheet: true };
  }

  setDrawerWidth(drawer, width);
  drawer.style.position = 'fixed';
  drawer.style.zIndex = 'var(--pb-z-dropdown)';
  return { drawer, titleId, isSheet: false };
}

/**
 * BC-6. `beside` (the default) opens next to an inspector row; `below` opens
 * under a control in the top bar, which has no room beside it.
 */
function setDrawerPlacement(drawer, placement) {
  if (placement === 'below') drawer.dataset.placement = 'below';
  else delete drawer.dataset.placement;
}

function positionDrawer(drawer, anchorEl) {
  // A bottom sheet is positioned by CSS against the viewport, not against a
  // row. Repositioning it would drag it back to the anchor on every resize.
  if (drawer.classList.contains('pb-sheet--nonmodal')) return;
  const rect = anchorEl.getBoundingClientRect();
  const drawerH = drawer.offsetHeight || 320;
  const drawerWidth = getDrawerWidth(drawer);
  if (drawer.dataset.placement === 'below') {
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - drawerWidth - 8);
    let top = rect.bottom + ANCHOR_GAP;
    if (top + drawerH > window.innerHeight - 8) {
      top = Math.max(8, window.innerHeight - drawerH - 8);
    }
    drawer.style.left = `${Math.max(8, left)}px`;
    drawer.style.top = `${top}px`;
    return;
  }
  let left = rect.right + ANCHOR_GAP;
  if (left + drawerWidth > window.innerWidth - 8) {
    // Flip to the LEFT of the anchor when the right side overflows.
    left = Math.max(8, rect.left - drawerWidth - ANCHOR_GAP);
  }
  let top = rect.top;
  if (top + drawerH > window.innerHeight - 8) {
    top = Math.max(8, window.innerHeight - drawerH - 8);
  }
  drawer.style.left = `${left}px`;
  drawer.style.top = `${top}px`;
}

/**
 * Open a drawer for `id`. If a drawer is already open with a *different* id,
 * cross-fade swap the body and update the title. If the same id is opened
 * twice in a row, close the active drawer.
 *
 * @param {{
 *   id: string,
 *   title: string,
 *   anchorEl: HTMLElement,
 *   triggerEl: HTMLElement,
 *   width?: number,
 *   render: (bodyEl: HTMLElement) => (void | (() => void) | Promise<void | (() => void)>),
 *   onClose?: () => void,
 *   beforeClose?: () => void,
 * }} opts
 *
 * `render` may return (or resolve to) a teardown function. It runs when the
 * drawer closes OR when the body is replaced by a swap. Use it to detach
 * eventBus listeners attached during render.
 *
 * @returns {() => void} close function
 */
export function openSectionDrawer(opts) {
  const { id, title, anchorEl, triggerEl, width, render, onClose, beforeClose, placement } =
    opts || {};
  if (!id || !title || !anchorEl || !triggerEl || typeof render !== 'function') {
    console.warn(
      '[section-properties drawer] openSectionDrawer requires id/title/anchorEl/triggerEl/render'
    );
    return () => {};
  }

  if (_activeDrawer && _activeDrawer.id === id) {
    const close = _activeDrawer.close;
    close({ restoreFocus: true });
    return close;
  }

  if (_activeDrawer && _activeDrawer.id !== id) {
    // Placement first: the swap repositions against the new anchor.
    if (_activeDrawer.drawer) setDrawerPlacement(_activeDrawer.drawer, placement);
    return _swapDrawer({
      id,
      title,
      anchorEl,
      triggerEl,
      width,
      render,
      onClose,
      beforeClose
    });
  }

  return _mountFreshDrawer({
    id,
    title,
    anchorEl,
    triggerEl,
    width,
    render,
    onClose,
    beforeClose,
    placement
  });
}

// Restore focus when a drawer closes. The trigger row can be unmounted while
// the drawer is open (the section inspector re-renders on state changes), so
// try a fallback chain instead of silently dropping focus to <body>:
//   1. the originating trigger row (if still connected)
//   2. the element focused when the drawer opened (previousFocus)
//   3. the panel-left region that hosts the inspector (stable, always present)
function _restoreDrawerFocus(current) {
  const candidates = [current.triggerEl, current.previousFocus];
  for (const el of candidates) {
    if (el && el.isConnected && typeof el.focus === 'function') {
      el.focus({ preventScroll: true });
      return;
    }
  }
  const panel = document.getElementById('pb-palette');
  if (panel) {
    // Make the region programmatically focusable without joining the tab order.
    if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
    panel.focus({ preventScroll: true });
  }
}

function _mountFreshDrawer({
  id,
  title,
  anchorEl,
  triggerEl,
  width,
  render,
  onClose,
  beforeClose,
  placement
}) {
  const { drawer, titleId, isSheet } = buildDrawerShell({ id, title, width });
  setDrawerPlacement(drawer, placement);
  const previousFocus = document.activeElement;
  document.body.appendChild(drawer);

  const body = drawer.querySelector('[data-spi-drawer-body]');
  const closeBtn = drawer.querySelector('.pb-spi-drawer-close');

  positionDrawer(drawer, anchorEl);

  const currentAnchorForDrawer = () =>
    _activeDrawer && _activeDrawer.drawer === drawer && _activeDrawer.anchorEl
      ? _activeDrawer.anchorEl
      : anchorEl;
  let repositionQueued = false;
  const queueDrawerPosition = () => {
    if (repositionQueued) return;
    repositionQueued = true;
    requestAnimationFrame(() => {
      repositionQueued = false;
      if (drawer.isConnected) positionDrawer(drawer, currentAnchorForDrawer());
    });
  };

  // Slide-in animation, gated on prefers-reduced-motion.
  drawer.classList.add('pb-spi-drawer-entering');
  if (!prefersReducedMotion()) {
    requestAnimationFrame(() => {
      drawer.classList.add('pb-spi-drawer-open');
      drawer.classList.remove('pb-spi-drawer-entering');
    });
  } else {
    drawer.classList.add('pb-spi-drawer-open');
    drawer.classList.remove('pb-spi-drawer-entering');
  }

  // Mount caller content into body. If render returns a function (or a
  // promise that resolves to one), treat it as a body-cleanup hook that
  // runs on swap or close.
  Promise.resolve(render(body))
    .then((teardown) => {
      if (_activeDrawer && _activeDrawer.id === id && typeof teardown === 'function') {
        _activeDrawer.bodyCleanup = teardown;
      }
    })
    .catch((err) => {
      console.error('[section-properties drawer] render threw:', err);
    })
    .finally(queueDrawerPosition);

  // Mark trigger as active.
  triggerEl.setAttribute('aria-expanded', 'true');
  triggerEl.classList.add('pb-spi-row-drawer-active');

  // Mobile P13 — drag-to-resize/dismiss on the phone sheet.
  //
  // The drawer keeps its focus trap either way: this is "non-modal" in the
  // SCRIM sense (no overlay, so the canvas above stays visible and live), not
  // in the focus sense. Changing the focus model here would not be additive,
  // and the trap is the drawer's existing contract on every viewport.
  let sheetDrag = null;
  if (isSheet) {
    let heights = computeSheetDetents(usableSheetHeight());
    sheetDrag = attachSheetDrag({
      card: drawer,
      grabber: drawer.querySelector('.pb-sheet-grabber'),
      header: drawer.querySelector('.pb-spi-drawer-header'),
      body,
      detents: detentOffsets(heights),
      slackPx: 48,
      onDismiss: () => closeFn({ restoreFocus: true }),
      onDragStart: () => {
        heights = computeSheetDetents(usableSheetHeight());
        drawer.style.setProperty('--pb-sheet-h', `${Math.round(heights.full)}px`);
        const index = SHEET_DETENT_NAMES.indexOf(drawer.dataset.pbDetent || 'half');
        return detentOffsets(heights)[index >= 0 ? index : 1];
      },
      onSnap: (_offset, index) => {
        applySheetDetent(drawer, SHEET_DETENT_NAMES[index] || 'half', heights);
      }
    });
  }

  // Focus trap + Escape.
  const releaseTrap = trapFocus(drawer);
  const onTrapEscape = () => closeFn({ restoreFocus: true });
  drawer.addEventListener('trap-escape', onTrapEscape);

  // Close button.
  closeBtn.addEventListener('click', () => closeFn({ restoreFocus: true }));

  // Click outside drawer + outside the panel that holds the trigger →
  // close. Clicks INSIDE the panel (e.g. on a different row) are handled
  // separately by row click handlers, which call openSectionDrawer again
  // and route into the swap path.
  const onDocPointerDown = (e) => {
    const currentAnchor =
      _activeDrawer && _activeDrawer.drawer === drawer && _activeDrawer.anchorEl
        ? _activeDrawer.anchorEl
        : anchorEl;
    if (isDrawerPointerInside(e, drawer, currentAnchor)) return;
    closeFn({ restoreFocus: false });
  };

  // Reposition on resize / scroll so the drawer tracks the anchor.
  const onWinChange = () => {
    positionDrawer(drawer, currentAnchorForDrawer());
  };

  const resizeObserver =
    typeof ResizeObserver !== 'undefined' ? new ResizeObserver(queueDrawerPosition) : null;
  resizeObserver?.observe(drawer);

  // Defer pointerdown wiring so the click that opened us doesn't immediately
  // close us.
  let pointerListenersInstalled = false;
  const pointerListenerFrame = requestAnimationFrame(() => {
    if (!_activeDrawer || _activeDrawer.drawer !== drawer) return;
    // One pointerdown replaces the mousedown+touchstart pair, which fired
    // TWICE on hybrid devices that report both.
    document.addEventListener('pointerdown', onDocPointerDown, true);
    pointerListenersInstalled = true;
  });
  window.addEventListener('resize', onWinChange);
  window.addEventListener('scroll', onWinChange, true);

  let closed = false;
  function closeFn({ restoreFocus = true } = {}) {
    if (closed) return;
    closed = true;

    const current =
      _activeDrawer && _activeDrawer.drawer === drawer
        ? _activeDrawer
        : {
            id,
            triggerEl,
            previousFocus,
            beforeClose,
            bodyCleanup: null,
            onClose
          };

    if (typeof current.beforeClose === 'function') {
      try {
        current.beforeClose();
      } catch (err) {
        console.error('[section-properties drawer] beforeClose threw:', err);
      }
    }
    if (typeof current.bodyCleanup === 'function') {
      try {
        current.bodyCleanup();
      } catch (err) {
        console.error('[section-properties drawer] bodyCleanup threw:', err);
      }
      current.bodyCleanup = null;
      if (_activeDrawer && _activeDrawer.drawer === drawer) {
        _activeDrawer.bodyCleanup = null;
      }
    }
    if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(pointerListenerFrame);
    if (pointerListenersInstalled) {
      document.removeEventListener('pointerdown', onDocPointerDown, true);
    }
    window.removeEventListener('resize', onWinChange);
    window.removeEventListener('scroll', onWinChange, true);
    resizeObserver?.disconnect();
    drawer.removeEventListener('trap-escape', onTrapEscape);
    sheetDrag?.destroy();
    releaseTrap();
    current.triggerEl?.setAttribute('aria-expanded', 'false');
    current.triggerEl?.classList.remove('pb-spi-row-drawer-active');

    if (!prefersReducedMotion()) {
      drawer.classList.remove('pb-spi-drawer-open');
      drawer.classList.add('pb-spi-drawer-leaving');
      setTimeout(() => drawer.remove(), ANIMATION_MS);
    } else {
      drawer.remove();
    }

    if (_activeDrawer && _activeDrawer.drawer === drawer) _activeDrawer = null;
    if (typeof current.onClose === 'function') {
      try {
        current.onClose();
      } catch (err) {
        console.error('[section-properties drawer] onClose threw:', err);
      }
    }
    if (restoreFocus) _restoreDrawerFocus(current);
  }

  _activeDrawer = {
    id,
    title,
    titleId,
    drawer,
    body,
    triggerEl,
    previousFocus,
    anchorEl,
    close: closeFn,
    beforeClose,
    onClose,
    // Kept so refreshActiveSectionDrawerBody() can re-run it against live
    // state (16B2 fix): a bulk drawer whose selection changed underneath it
    // must not keep editing the ids it captured at open time.
    render,
    bodyCleanup: null
  };

  return closeFn;
}

function _swapDrawer({ id, title, anchorEl, triggerEl, width, render, onClose, beforeClose }) {
  if (_swapPending) return _activeDrawer.close;
  _swapPending = true;

  const prev = _activeDrawer;
  // Honor previous drawer's beforeClose (so editors can commit transactions
  // before their DOM is destroyed).
  if (typeof prev.beforeClose === 'function') {
    try {
      prev.beforeClose();
    } catch (err) {
      console.error('[section-properties drawer] beforeClose threw:', err);
    }
  }
  // Run the previous body's cleanup so its eventBus listeners detach before
  // its DOM is replaced.
  if (typeof prev.bodyCleanup === 'function') {
    try {
      prev.bodyCleanup();
    } catch (err) {
      console.error('[section-properties drawer] bodyCleanup threw:', err);
    }
    prev.bodyCleanup = null;
  }
  // Mark old trigger inactive, new trigger active.
  prev.triggerEl.setAttribute('aria-expanded', 'false');
  prev.triggerEl.classList.remove('pb-spi-row-drawer-active');
  triggerEl.setAttribute('aria-expanded', 'true');
  triggerEl.classList.add('pb-spi-row-drawer-active');
  setDrawerWidth(prev.drawer, width);
  positionDrawer(prev.drawer, anchorEl);

  const reduced = prefersReducedMotion();
  const fadeOutMs = reduced ? 0 : SWAP_FADE_MS;
  prev.body.classList.add('pb-spi-drawer-body-fading-out');

  setTimeout(() => {
    if (!_activeDrawer || _activeDrawer.drawer !== prev.drawer || !prev.drawer.isConnected) {
      _swapPending = false;
      return;
    }
    prev.body.innerHTML = '';
    // Update title text + DOM id reference (kept stable for assistive tech).
    const titleEl = prev.drawer.querySelector('.pb-spi-drawer-title');
    if (titleEl) titleEl.textContent = title;
    prev.body.classList.remove('pb-spi-drawer-body-fading-out');
    prev.body.classList.add('pb-spi-drawer-body-fading-in');

    Promise.resolve(render(prev.body))
      .then((teardown) => {
        if (_activeDrawer && _activeDrawer.id === id && typeof teardown === 'function') {
          _activeDrawer.bodyCleanup = teardown;
        }
      })
      .catch((err) => {
        console.error('[section-properties drawer] render threw:', err);
      });

    // Re-position in case the new content has different intrinsic height.
    requestAnimationFrame(() => {
      positionDrawer(prev.drawer, anchorEl);
    });

    // Update record bookkeeping (drawer + body persist). The close function
    // reads this record at close time so the active trigger / cleanup hooks
    // stay correct after row swaps.
    _activeDrawer = {
      id,
      title,
      titleId: prev.titleId,
      drawer: prev.drawer,
      body: prev.body,
      triggerEl,
      // Keep the ORIGINAL previousFocus across swaps — the user's pre-drawer
      // focus origin doesn't change when they switch rows mid-session.
      previousFocus: prev.previousFocus,
      anchorEl,
      close: prev.close,
      beforeClose,
      onClose,
      render,
      bodyCleanup: null
    };

    setTimeout(() => {
      prev.body.classList.remove('pb-spi-drawer-body-fading-in');
      _swapPending = false;
    }, fadeOutMs);
  }, fadeOutMs);

  // The close fn we return points at the now-mounted drawer; it's still the
  // original close because we reuse the same DOM container.
  return prev.close;
}

/**
 * Re-run the active drawer's render against LIVE state, keeping the drawer
 * open, its position, and its focus trap (16B2 fix).
 *
 * A bulk drawer captures nothing: `render` resolves the current selection
 * every time it runs, so replaying it is how a spacing/background/columns
 * drawer picks up a membership change ({A,B} → {A,C}) instead of continuing
 * to write the ids it opened with. The previous body's cleanup runs first so
 * its eventBus listeners detach before the DOM is replaced — the same
 * contract the swap path honors.
 *
 * @returns {boolean} whether a drawer was refreshed
 */
export function refreshActiveSectionDrawerBody() {
  const current = _activeDrawer;
  if (!current || typeof current.render !== 'function' || !current.body?.isConnected) return false;
  if (typeof current.bodyCleanup === 'function') {
    try {
      current.bodyCleanup();
    } catch (err) {
      console.error('[section-properties drawer] bodyCleanup threw:', err);
    }
    current.bodyCleanup = null;
  }
  current.body.innerHTML = '';
  Promise.resolve(current.render(current.body))
    .then((teardown) => {
      if (_activeDrawer === current && typeof teardown === 'function') {
        _activeDrawer.bodyCleanup = teardown;
      }
    })
    .catch((err) => {
      console.error('[section-properties drawer] render threw:', err);
    });
  return true;
}

/** Update the active drawer's visible title (count suffixes change live). */
export function setActiveSectionDrawerTitle(title) {
  if (!_activeDrawer || !title) return false;
  const titleEl = _activeDrawer.drawer?.querySelector('.pb-spi-drawer-title');
  if (titleEl) titleEl.textContent = title;
  _activeDrawer.title = title;
  return true;
}

/** Close the active section-properties drawer, if any. */
export function closeActiveSectionDrawer({ restoreFocus = true } = {}) {
  if (!_activeDrawer) return false;
  _activeDrawer.close({ restoreFocus });
  return true;
}

/** True when any section-properties drawer is currently open. */
export function isSectionDrawerOpen() {
  return _activeDrawer != null;
}

/** Returns the active drawer's id, or null. */
export function getActiveSectionDrawerId() {
  return _activeDrawer ? _activeDrawer.id : null;
}
